const WINDOW_SCHEDULE_API_URL = 'https://delidoor-oms--development.gadget.app/window-schedule';
const shippingExclusionDates = [
  "2025-12-23",
  "2025-12-24",
  "2025-12-25",
  "2025-12-26",
  "2026-01-01",
  "2026-01-02",
  "2026-04-04",  // Easter Saturday
  "2026-04-06",  // Easter Monday
  "2026-04-25"   // ANZAC Day
];

const shippingExclusionDatesAfterCutoff = [
  "2025-12-23",
  "2025-12-24",
  "2025-12-25",
  "2025-12-26",
  "2025-12-27",
  "2025-12-28",
  "2025-12-29",
  "2025-12-30",
  "2025-12-31",
  "2026-01-01",
  "2026-01-02",
  "2026-01-03",
  "2026-01-26",
  "2026-04-04",  // Easter Saturday
  "2026-04-06",  // Easter Monday
  "2026-04-25"   // ANZAC Day
];

async function fetchShippingWindowsForPostcode(postcode, now = new Date()) {
  if (!window.buildShippingWindowsApiUrl || !window.normalizeShippingWindowsFromApi) {
    return [];
  }

  const requestUrl = window.buildShippingWindowsApiUrl(postcode);
  if (!requestUrl) {
    return [];
  }

  const response = await fetch(requestUrl, { method: 'GET' });
  if (!response.ok) {
    throw new Error(`Failed to fetch shipping windows (${response.status})`);
  }

  const payload = await response.json();
  return window.normalizeShippingWindowsFromApi(payload, now);
}


function getSydneyDate(localDate = new Date()) {
  // Sydney is UTC+10 or UTC+11 (with daylight saving).
  // We'll use Intl.DateTimeFormat to get the offset dynamically.
  const sydneyFormatter = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney', 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit',
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit', 
    hour12: false
  });

  // Break down the formatted string to get actual datetime
  const parts = sydneyFormatter.formatToParts(localDate);
  const dateTime = {};
  parts.forEach(({type, value}) => { dateTime[type] = value; });

  // Construct a Date object in Sydney's local time
  const sydneyDate = new Date(
    Number(dateTime.year),
    Number(dateTime.month) - 1,
    Number(dateTime.day),
    Number(dateTime.hour),
    Number(dateTime.minute),
    Number(dateTime.second)
  );
  
  return sydneyDate;
}

/**
 * Custom element for handling cart remove button functionality
 * @class CartRemoveButton
 * @extends HTMLElement
 */
class CartRemoveButton extends HTMLElement {
  /**
   * Creates an instance of CartRemoveButton
   * @constructor
   */
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems =
        this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);


function getFromLocalStorage(key) {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
}


/**
 * Custom element for handling cart items functionality
 * @class CartItems
 * @extends HTMLElement
 */
class CartItems extends HTMLElement {
  /**
   * Creates an instance of CartItems
   * @constructor
   */
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  /**
   * Called when the element is connected to the DOM
   */
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        // Don't fire this as this will mess up the cart drawer  if event source is from product-card
        if (event.source === 'cart-items' || event.source === 'product-card') {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  /**
   * Called when the element is disconnected from the DOM
   */
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  /**
   * Handles change events on cart item inputs
   * @param {Event} event - The change event
   */
  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name')
    );
  }

  /**
   * Handles cart update events
   */
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  /**
   * Gets the sections that need to be rendered
   * @returns {Array} Array of section objects
   */
  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  /**
   * Updates the quantity of a cart item
   * @param {number} line - The line item index
   * @param {number} quantity - The new quantity
   * @param {string} name - The name attribute of the input
   */
  updateQuantity(line, quantity, name) {
    this.enableLoading(line);
    this.querySelectorAll('.quantity__button').forEach((button) =>
      button.classList.add('disabled')
    );

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');
        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        parsedState.items.forEach((item) => {
          const productCards = document.querySelectorAll(
            "[data-variant-id='" + item.variant_id + "']"
          );
          productCards.forEach((productCard) => {
            if (productCard == null || productCard == undefined) return;
            const quantityInput = productCard.querySelector('.quantity_input');
            if (quantityInput) {
              quantityInput.value = item.quantity;
            }
          });
        });

        if (quantity == 0) {
          parsedState.items_removed.forEach((item) => {
            const productCards = document.querySelectorAll(
              "[data-variant-id='" + item.variant_id + "']"
            );
            productCards.forEach((productCard) => {
              if (productCard == null || productCard == undefined) return;
              const buyButton = productCard.querySelector('.buy-button');
              const changeButton = productCard.querySelector('.change-button');
              const quantityInput = productCard.querySelector('.quantity_input');
              
              if (buyButton) buyButton.classList.add('active');
              if (changeButton) changeButton.classList.remove('active');
              if (quantityInput) quantityInput.value = 0;
            });
          });
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter)
          cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            'is-empty',
            parsedState.item_count === 0
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = '';
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              '[quantity]',
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector('.drawer__inner-empty'),
            cartDrawerWrapper.querySelector('a')
          );
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector('.cart-item__name')
          );
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });

        // For refactoring -- Totally create a new copy of cart.js
        window.refreshCartAccordion && window.refreshCartAccordion();
      })
      .catch((err) => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) =>
          overlay.classList.add('hidden')
        );
        this.querySelectorAll('.quantity__button').forEach((button) =>
          button.classList.remove('disabled')
        );
        const errors =
          document.getElementById('cart-errors') ||
          document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
        console.log(err);
      })
      .finally(() => {
        this.querySelectorAll('.quantity__button').forEach((button) =>
          button.classList.remove('disabled')
        );
        this.disableLoading(line);
      });
  }

  /**
   * Updates live regions for accessibility
   * @param {number} line - The line item index
   * @param {string} message - The message to display
   */
  updateLiveRegions(line, message) {
    discountApply();
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  /**
   * Gets the inner HTML of a section
   * @param {string} html - The HTML content
   * @param {string} selector - The selector to find the section
   * @returns {string} The inner HTML of the section
   */
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  /**
   * Enables loading state for a cart item
   * @param {number} line - The line item index
   */
  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove('hidden')
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  /**
   * Disables loading state for a cart item
   * @param {number} line - The line item index
   */
  disableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add('hidden')
    );
  }
}

customElements.define('cart-items', CartItems);

/**
 * Custom element for handling cart notes
 * @class CartNote
 * @extends HTMLElement
 * @description A web component that handles cart note updates with debounced
 * API calls to prevent excessive server requests. Automatically saves notes
 * as the user types with a configurable delay.
 * 
 * @example
 * <cart-note>
 *   <textarea placeholder="Add a note to your order..."></textarea>
 * </cart-note>
 * 
 * @example
 * // With custom styling
 * <cart-note class="cart-note--fullwidth">
 *   <label for="cart-note">Order Notes</label>
 *   <textarea id="cart-note" rows="3" maxlength="500"></textarea>
 * </cart-note>
 * 
 * @fires change - Dispatched when note content changes
 * @listens change - Listens for input changes to trigger API updates
 */
if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      /**
       * Creates an instance of CartNote
       * @constructor
       * @description Initializes the cart note component with debounced change handling
       */
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

/**
 * Custom element for handling shipping window lookup functionality
 * @class ShippingWindowLookup
 * @extends HTMLElement
 * @description A comprehensive web component that manages delivery location lookup,
 * shipping window calculations, and delivery information display. Integrates with
 * external APIs to provide real-time delivery scheduling information. Supports
 * both standalone and embedded page modes.
 * 
 * @example
 * <shipping-window-lookup data-is-page="true">
 *   <input type="text" placeholder="Enter your postcode..." />
 *   <div class="results-container hidden">
 *     <div class="autocomplete-results"></div>
 *   </div>
 *   <div class="delivery-info hidden">
 *     <p class="delivery-info__msg"></p>
 *     <p class="delivery-info__delivery-window-msg"></p>
 *   </div>
 * </shipping-window-lookup>
 * 
 * @example
 * // Standalone mode (not embedded in delivery page)
 * <shipping-window-lookup>
 *   <button class="btn--delivery-info">Check Delivery</button>
 *   <div class="form-control hidden">
 *     <input type="text" placeholder="Enter postcode..." />
 *   </div>
 * </shipping-window-lookup>
 * 
 * @fires deliveryLocationSelected - Dispatched when a delivery location is selected
 * @fires deliveryWindowsLoaded - Dispatched when delivery windows are loaded
 * @listens shippingWindowsLoaded - Listens for shipping windows data updates
 * 
 * @property {boolean} isEmbeddedInDeliveryPage - Whether component is embedded in delivery page
 * @property {Object|null} existingDeliveryLocation - Previously saved delivery location
 * @property {Object|null} existingFirstDeliveryWindow - Previously saved delivery window
 */
if (!customElements.get('shipping-window-lookup')) {
  class ShippingWindowLookup extends HTMLElement {
    /**
     * Creates an instance of ShippingWindowLookup
     * @constructor
     */
    constructor() {
      super();

      this.isEmbeddedInDeliveryPage = this.dataset.isPage;
      this.instanceName = this.dataset.instanceName;
      this.existingDeliveryLocation = null;
      this.existingFirstDeliveryWindow = null;
      this.deliveryLocationUnsubscriber = undefined;

      console.log(
        'cdebug this.existingDeliveryLocation',
        this.existingDeliveryLocation
      );
      console.log(
        'cdebug this.existingFirstDeliveryWindow',
        this.existingFirstDeliveryWindow
      );

      this.resultsToggleBtn = this.querySelector('.btn--delivery-info');
      this.resultsContainer = this.querySelector('.results-container');
      this.searchInputContainer = this.querySelector('.form-control');
      this.searchInput = this.querySelector('input');
      this.resultsContainer = this.querySelector('.results-container');
      this.autocompleteResultsContainer = this.querySelector(
        '.autocomplete-results'
      );
      this.changePostcodeBtn = this.querySelector('.change-postcode-btn');
      this.errorMessage = this.querySelector('.delivery-info__msg--error');
      this.deliveryInfoMessage = this.querySelector('.delivery-info__msg');
      this.deliveryInfoWindowMessage = this.querySelector(
        '.delivery-info__delivery-window-msg'
      );
      this.loadingSpinner = this.querySelector('.loading-spinner');

      // Embedded page controls
      this.deliveryInfoBody = document.querySelector('.delivery-info_body');
      this.deliveryInfoMessagePage = document.querySelector(
        '.delivery-info_body .delivery-info_msg'
      );
      this.nextDeliveryInfoContainer = document.querySelector(
        '.delivery-info--next .info-item_list'
      );
      this.deliveryScheduleContainer = document.querySelector(
        '.delivery-info--schedule .delivery-info_info'
      );
    }

    /**
     * Hides or shows the delivery info body
     * @param {boolean} hide - Whether to hide the delivery info body
     * @description Controls the visibility of the delivery information section
     * when embedded in a delivery page
     */
    hideDeliveryInfoBody(hide) {
      if (hide) {
        this.deliveryInfoBody.classList.add('hidden');
      } else {
        this.deliveryInfoBody.classList.remove('hidden');
      }
    }

    /**
     * Displays shipping window information in the main component
     * @param {Object} firstDeliveryWindow - The first delivery window object
     * @param {Object} selectedPostcodeSuburb - The selected postcode and suburb
     * @param {Date} now - The current date
     * @description Formats and displays delivery information including next
     * delivery date and order cutoff times
     */
    displayShippingWindowInformation(
      firstDeliveryWindow,
      selectedPostcodeSuburb,
      now
    ) {
      let {
        nextDeliveryDate,
        orderCutoffDate,
        orderCutoffDateToday,
        cutoffTimeAP,
      } = getDeliveryAndCutoffDates(firstDeliveryWindow);
      let formattedNextDeliveryDate = formatDatePrettyWithYear(nextDeliveryDate);
      let formattedOrderCutoffDate = formatDatePretty(orderCutoffDate);

      console.log('nextDeliveryDate', nextDeliveryDate);
      console.log('orderCutoffDate', orderCutoffDate);
      console.log('orderCutoffDateToday', orderCutoffDateToday);
      console.log('formattedNextDeliveryDate', formattedNextDeliveryDate);

      this.deliveryInfoMessage.innerHTML =
        window.shippingWindowsCopy.deliveryInfoMessage
          .replace('{suburb}', selectedPostcodeSuburb.suburb)
          .replace('{postcode}', selectedPostcodeSuburb.postcode);

      this.deliveryInfoWindowMessage.innerHTML =
        window.shippingWindowsCopy.deliveryFirstSchedule
          .replace('{deliveryDate}', formattedNextDeliveryDate)
          .replace('{formattedOrderCutoffDate}', formattedOrderCutoffDate)
          .replace('{cutoffTimeAP}', cutoffTimeAP);

      this.deliveryInfoMessage.classList.remove('hidden');
      this.deliveryInfoWindowMessage.classList.remove('hidden');
    }

    /**
     * Displays the delivery schedule for a zone
     * @param {Array} zoneDeliverySchedules - The delivery schedules for the zone
     */
    displayZoneDeliverySchedule(zoneDeliverySchedules) {
      let output = ``;

      for (let schedule of zoneDeliverySchedules) {
        let { timeStartAP, timeEndAP } = getFormattedTimes(schedule);
        output += `<div class="info-item_list">
								<div class="item_top">${schedule.weekdayTitle}</div>
								<div class="item_inner">${timeStartAP} - ${timeEndAP}</div>
							</div>`;
      }

      this.deliveryScheduleContainer.innerHTML = output;
    }

    /**
     * Displays shipping window info for the page
     * @param {Object} firstDeliveryWindow - The first delivery window object
     * @param {Object} selectedPostcodeSuburb - The selected postcode and suburb
     * @param {Date} now - The current date
     */
    displayShippingWindowInfoForPage(
      firstDeliveryWindow,
      selectedPostcodeSuburb,
      now
    ) {
      let {
        nextDeliveryDate,
        orderCutoffDate,
        orderCutoffDateToday,
        cutoffTimeAP,
      } = getDeliveryAndCutoffDates(firstDeliveryWindow);
      let formattedNextDeliveryDate =
        formatDatePrettyWithYear(nextDeliveryDate);
      let { timeStartAP, timeEndAP } = getFormattedTimes(firstDeliveryWindow);

      console.log('cdebug nextDeliveryDate', nextDeliveryDate);
      console.log('cdebug orderCutoffDate', orderCutoffDate);
      console.log('cdebug displayShippingWindowInfoForPage', selectedPostcodeSuburb);

      // window.shippingWindowsCopy.deliveryInfoMessage's format is `<p class="text-center">Yes! Here's the delivery information for  <span class="suburb">${selectedPostcodeSuburb.suburb} (${selectedPostcodeSuburb.postcode})</span></p>`;
      this.deliveryInfoMessagePage.innerHTML =
        window.shippingWindowsCopy.deliveryInfoMessage
          .replace('{suburb}', selectedPostcodeSuburb.suburb)
          .replace('{postcode}', selectedPostcodeSuburb.postcode);

      this.nextDeliveryInfoContainer.innerHTML = `<div class="item_top">${formattedNextDeliveryDate}</div><div class="item_inner">${timeStartAP} - ${timeEndAP}</div>`;
    }

    /**
     * Handles the results toggle button click
     * @param {Event} e - The click event
     */
    handleResultsToggleClick(e) {
      this.resultsContainer.classList.toggle('hidden');

      // For now, focus right away
      if (!this.resultsContainer.classList.contains('hidden')) {
        this.searchInput && this.searchInput.focus();
      }
    }

    /**
     * Handles input field changes
     * @param {Event} e - The input event
     */
    handleInputFieldChange(e) {
      const searchTerm = e.target.value;

      const search = (results, term) => {
        if (isNaN(term)) {
          return results
            .filter((item) => {
              const name = `${item.suburb} ${item.postcode}`;
              return name.toLowerCase().startsWith(term.toLowerCase());
            });
        }
        return results
          .filter((item) => {
            return item?.postcode?.startsWith(term);
          });
      };

      var filtered = search(window.shippingDataZones, `${searchTerm}`);

      const generateResultsElements = (filtered) => {
        let results = '';
        filtered.forEach(function (item) {
          results += `<li class="autocomplete-result">
								<button class="autocomplete-result-btn" data-name="${item.suburb} ${item.postcode}"
								data-suburb="${item.suburb}" data-postcode="${item.postcode}" data-zone="${item.zone}">${item.suburb} ${item.zone} ${item.postcode}</button>
							  </li>
							`;
        });

        return results;
      };

      this.autocompleteResultsContainer.innerHTML = '';

      const resultElements = generateResultsElements(filtered);
      if (searchTerm && resultElements) {
        this.autocompleteResultsContainer.innerHTML = resultElements;
      }

      if (filtered.length == 0) {
        this.errorMessage.classList.remove('hidden');
        if (this.isEmbeddedInDeliveryPage) {
          this.hideDeliveryInfoBody(true);
        }
      } else {
        this.errorMessage.classList.add('hidden');
      }
    }

    /**
     * Renders the results for a given delivery location
     * @param {Object} incomingDeliveryLocation - The delivery location object
     * @param {Object} [incomingFirstShippingWindow=null] - The first shipping window object
     */
    async renderResults(
      incomingDeliveryLocation,
      incomingFirstShippingWindow = null
    ) {
      console.log(
        'cdebug renderResults...',
        incomingDeliveryLocation,
        incomingFirstShippingWindow
      );
      const { zone, suburb, postcode } = incomingDeliveryLocation;
      const sydneyDate = getSydneyDate();
      let now = new Date();
      if (window.overrideDate) {
        now = new Date(window.overrideDate);
      }
      // Check if it's 23rd Dec (any year), after or equal to 8pm
      const cutoffDateUTC = Date.UTC(2025, 11, 23, 9, 0, 0); // December 23, 2025, 9am UTC
      const isAfterCutoff = sydneyDate.getTime() > cutoffDateUTC;

      this.changePostcodeBtn.classList.remove('hidden');
      this.searchInputContainer.classList.add('hidden');

      this.autocompleteResultsContainer.innerHTML = '';
      this.loadingSpinner.classList.remove('hidden');

      // Check first if deliveryZone and delivery windows are available
      let shippingZoneIsAvailable = false;
      let normalizedShippingWindows = [];
      let resultData = null;

      try {
        // Reset previous postcode windows before hydrating new data.
        window.shippingWindows = [];
        normalizedShippingWindows = await fetchShippingWindowsForPostcode(postcode, now);
        window.shippingWindows = normalizedShippingWindows;

        const result = await fetch(WINDOW_SCHEDULE_API_URL, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            postcode,
            numWindows: 90,
            numDays: 45
          })
        });

       resultData = await result.json();

       if (!Array.isArray(resultData?.data)) {
        resultData = { data: [] };
       }

       if(isAfterCutoff) {
        resultData.data = resultData.data.filter(window => !shippingExclusionDatesAfterCutoff.includes(window.deliveryDate));
       } else {
        resultData.data = resultData.data.filter(window => !shippingExclusionDates.includes(window.deliveryDate));
       }

      console.log('cdebug resultData.data (after blackout filter)', resultData.data);

      console.log('cdebug resultData', resultData);

      shippingZoneIsAvailable = normalizedShippingWindows.length > 0;


      } catch(error) {
        console.error('cdebug error', error);
      }

      let firstDeliveryWindow = {};
      let selectedPostcodeSuburb = { suburb, postcode, zone };
      let deliveryZoneDeliveryDates = [];

      console.log('cdebug shippingZoneIsAvailable', shippingZoneIsAvailable);

      // Hide loading spinner
      this.loadingSpinner.classList.add('hidden');

      // If deliveryZone is available, save the data and print out the information
      if (shippingZoneIsAvailable) {
        setPostcodeSuburbAsCartAttribute({
          postcode,
          suburb,
        });
        saveToLocalStorage('delivery_location', selectedPostcodeSuburb);

        firstDeliveryWindow =
          Array.isArray(resultData?.data) && resultData.data.length > 0
            ? resultData.data[0]
            : findFirstDeliveryWindow(now, zone) || {};

        if (firstDeliveryWindow) {
          console.log('cdebug firstDeliveryWindow', firstDeliveryWindow);
          saveToLocalStorage('first_delivery_window', firstDeliveryWindow);

          console.log('cdebug', window.shippingWindows);

          if (this.isEmbeddedInDeliveryPage) {
            deliveryZoneDeliveryDates = sortByWeekdayId(
              findZoneDeliveryDates(now, zone)
            );

            this.deliveryInfoMessage.classList.add('hidden');
            this.deliveryInfoWindowMessage.classList.add('hidden');

            this.hideDeliveryInfoBody(false);
            this.displayShippingWindowInfoForPage(
              firstDeliveryWindow,
              selectedPostcodeSuburb,
              now
            );
            this.displayZoneDeliverySchedule(deliveryZoneDeliveryDates);
          } else {
            this.displayShippingWindowInformation(
              firstDeliveryWindow,
              selectedPostcodeSuburb,
              now
            );
          }
        } else {
          this.errorMessage.classList.remove('hidden');
          if (this.isEmbeddedInDeliveryPage) {
            this.hideDeliveryInfoBody(true);
          }
        }
      } else {
        // If deliveryZone is not available, show error message
        this.errorMessage.classList.remove('hidden');
      }
    }

    /**
     * Handles the delivery location selected event
     * @param {Object} data - The event data object
     */
    handleDeliveryLocationSelected(data) {

      const { suburb, postcode, zone, instanceSource } = data;
      console.log('cdebug handleDeliveryLocationSelected', data, this.instanceName, instanceSource);

      // If the instance of shipping-window-lookup is not the same as the instance that broadcasted the event, then render the results
      if (instanceSource !== this.instanceName) {
        this.renderResults({
          suburb,
          postcode,
          zone,
        });
      }
    }

    /**
     * Handles autocomplete results click
     * @param {Event} e - The click event
     */
    handleAutocompleteResultsClick(e) {
      const button = e.target.closest('button');
      const suburb = button.dataset.suburb;
      const postcode = button.dataset.postcode;
      const zone = button.dataset.zone;

      this.renderResults({
        suburb,
        postcode,
        zone,
      });

      // Broadcast the event to other instances of shipping-window-lookup using pubsub
      publish(PUB_SUB_EVENTS.deliveryLocationSelected, {
        instanceSource: this.instanceName,
        suburb,
        postcode,
        zone,
      });
    }

    /**
     * Handles the change postcode button click
     * @param {Event} e - The click event
     */
    handleChangeBtnClick(e) {
      this.searchInputContainer.classList.remove('hidden');
      this.changePostcodeBtn.classList.add('hidden');
      this.searchInput.value = '';
      this.searchInput && this.searchInput.focus();

      if (this.isEmbeddedInDeliveryPage) {
        this.hideDeliveryInfoBody(true);
      } else {
        this.deliveryInfoMessage.classList.add('hidden');
        this.deliveryInfoWindowMessage.classList.add('hidden');
        this.errorMessage.classList.add('hidden');
        this.deliveryInfoMessage.innerHTML = '';
        this.deliveryInfoWindowMessage.innerHTML = '';
      }
    }

    /**
     * Called when the element is connected to the DOM
     */
    connectedCallback() {
      this.existingDeliveryLocation = getFromLocalStorage('delivery_location');
      this.existingFirstDeliveryWindow = getFromLocalStorage(
        'first_delivery_window'
      );

      console.log(
        'cdebug this.existingDeliveryLocation',
        this.existingDeliveryLocation
      );
      console.log(
        'cdebug this.existingFirstDeliveryWindow',
        this.existingFirstDeliveryWindow
      );

      const onResultsToggleClick = this.handleResultsToggleClick.bind(this);
      const onInputChange = this.handleInputFieldChange.bind(this);
      const onAutocompleteResultsClick =
        this.handleAutocompleteResultsClick.bind(this);
      const onHandleChangeBtnClick = this.handleChangeBtnClick.bind(this);

      // Initialize any necessary elements or event listeners when the element is added to the DOM
      this.resultsToggleBtn.addEventListener('click', onResultsToggleClick);
      this.searchInput.addEventListener('keyup', onInputChange);
      this.autocompleteResultsContainer.addEventListener(
        'click',
        onAutocompleteResultsClick
      );
      this.changePostcodeBtn.addEventListener('click', onHandleChangeBtnClick);

      console.log('cdebug this.instanceName', this.instanceName);
      this.deliveryLocationUnsubscriber = subscribe(
        PUB_SUB_EVENTS.deliveryLocationSelected,
        this.handleDeliveryLocationSelected.bind(this)
      );

      if (this.existingDeliveryLocation && this.existingFirstDeliveryWindow) {
        this.resultsContainer.classList.remove('hidden');
        this.renderResults(
          this.existingDeliveryLocation,
          this.existingFirstDeliveryWindow
        );
      }
    }

    /**
     * Called when shipping windows are loaded
     * @param {Object} data - The shipping windows data
     */
    onShippingWindowsLoaded(data) {
      console.log('cdebug onShippingWindowsLoaded', data);
      let firstDeliveryWindow = {};
      let now = new Date();
      if (window.overrideDate) {
        now = new Date(window.overrideDate);
      }

      if (this.existingDeliveryLocation && this.existingFirstDeliveryWindow) {
        let { zone } = this.existingDeliveryLocation;
        this.renderResults(
          this.existingDeliveryLocation,
          this.existingFirstDeliveryWindow
        );

        // Double check delivery window data to make sure correctness as data from localstorage might be stale.
        firstDeliveryWindow = findFirstDeliveryWindow(now, zone);
        this.renderResults(this.existingDeliveryLocation, firstDeliveryWindow);
      }
    }

    /**
     * Called when the element is disconnected from the DOM
     */
    disconnectedCallback() {
      // Clean up any event listeners or resources when the element is removed from the DOM
      if (this.deliveryLocationUnsubscriber) {
        this.deliveryLocationUnsubscriber();
      }
    }
  }

  customElements.define('shipping-window-lookup', ShippingWindowLookup);
}
